import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Trophy, Award, Medal } from "lucide-react";
import { TierBadge } from "./tier-badge";

type LeaderboardUser = {
  rank: number;
  userId: string;
  firstName: string | null;
  lastName: string | null;
  username: string;
  profilePictureUrl: string | null;
  tier: 'Gold' | 'Silver' | 'Bronze';
  totalPoints: number;
};

export function LeaderboardPreview() {
  // Check if user is authenticated before making API call
  const hasToken = typeof window !== 'undefined' && !!localStorage.getItem('authToken');
  
  const { data, isLoading, error } = useQuery<{ period: string; users: LeaderboardUser[] }>({
    queryKey: ['/api/leaderboard', 'week', 3],
    queryFn: async () => {
      // Don't make API call if not authenticated
      if (!hasToken) {
        return { period: 'week', users: [] };
      }
      
      try {
        const res = await apiRequest('GET', '/api/leaderboard?period=week&limit=3');
        return await res.json();
      } catch (err: any) {
        // Silently fail if not authenticated (401/403) or API error
        // Only log non-auth errors
        if (err?.message && !err.message.includes('401') && !err.message.includes('403')) {
          console.error('[Leaderboard] API error:', err);
        }
        return { period: 'week', users: [] };
      }
    },
    enabled: hasToken, // Only run query if user is authenticated
    retry: false,
    refetchOnWindowFocus: false,
  });

  if (isLoading || error || !data || data.users.length === 0) {
    return null;
  }

  return (
    <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-800">
      <div className="text-center mb-4">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
          Star Users This Week
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Ask questions and up/down vote to be on the leaderboard
        </p>
      </div>
      <div className="flex items-center justify-center gap-3">
        {data.users.slice(0, 3).map((user) => (
          <div
            key={user.userId}
            className="flex flex-col items-center gap-1.5 p-2 rounded-lg bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-800"
          >
            {user.profilePictureUrl ? (
              <img
                src={user.profilePictureUrl}
                alt={`${user.firstName || user.username}`}
                className="w-10 h-10 rounded-full object-cover border-2 border-gray-300 dark:border-gray-700"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-gray-300 dark:bg-gray-700 flex items-center justify-center border-2 border-gray-300 dark:border-gray-700">
                <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">
                  {(user.firstName?.[0] || user.lastName?.[0] || user.username[0]).toUpperCase()}
                </span>
              </div>
            )}
            <div className="text-center">
              <div className="text-xs font-medium text-gray-900 dark:text-gray-100">
                {user.firstName || user.lastName
                  ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
                  : user.username}
              </div>
              <TierBadge tier={user.tier} size="sm" showIcon={false} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

